import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { vetrineContentService } from '../../../services/vetrineContentService';
import { format } from 'date-fns';

const CashbackManager = ({ storeId }) => {
  const [cashbackOffers, setCashbackOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCashback, setEditingCashback] = useState(null);
  const [formData, setFormData] = useState({
    titolo: '',
    descrizione: '',
    tipo: 'percentuale',
    valore: '',
    importoMinimo: '',
    importoMassimo: '',
    condizioni: '',
    dataInizio: '',
    dataFine: '',
    stato: 'attiva',
    inEvidenza: false
  });

  useEffect(() => {
    loadCashbackOffers();
  }, [storeId]);

  const loadCashbackOffers = async () => {
    try {
      setLoading(true);
      const data = await vetrineContentService?.getCashbackOffersByStore(storeId);
      setCashbackOffers(data);
    } catch (error) {
      console.error('Error loading cashback offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      const cashbackData = {
        ...formData,
        storeId,
        valore: parseFloat(formData?.valore) || 0,
        importoMinimo: formData?.importoMinimo ? parseFloat(formData?.importoMinimo) : null,
        importoMassimo: formData?.importoMassimo ? parseFloat(formData?.importoMassimo) : null,
        dataInizio: formData?.dataInizio || null,
        dataFine: formData?.dataFine || null
      };

      if (editingCashback) {
        await vetrineContentService?.updateCashbackOffer(editingCashback?.id, cashbackData);
      } else {
        await vetrineContentService?.createCashbackOffer(cashbackData);
      }

      await loadCashbackOffers();
      resetForm();
    } catch (error) {
      console.error('Error saving cashback:', error);
      alert('Errore nel salvare il cashback: ' + error?.message);
    }
  };

  const handleEdit = (cashback) => {
    setEditingCashback(cashback);
    setFormData({
      titolo: cashback?.titolo || '',
      descrizione: cashback?.descrizione || '',
      tipo: cashback?.tipo || 'percentuale',
      valore: cashback?.valore || '',
      importoMinimo: cashback?.importoMinimo || '',
      importoMassimo: cashback?.importoMassimo || '',
      condizioni: cashback?.condizioni || '',
      dataInizio: cashback?.dataInizio ? cashback?.dataInizio?.split('T')?.[0] : '',
      dataFine: cashback?.dataFine ? cashback?.dataFine?.split('T')?.[0] : '',
      stato: cashback?.stato || 'attiva',
      inEvidenza: cashback?.inEvidenza ?? false
    });
    setShowForm(true);
  };

  const handleDelete = async (cashbackId) => {
    if (!confirm('Sei sicuro di voler eliminare questa offerta cashback?')) return;
    
    try {
      await vetrineContentService?.deleteCashbackOffer(cashbackId);
      await loadCashbackOffers();
    } catch (error) {
      console.error('Error deleting cashback:', error);
      alert('Errore nell\'eliminare il cashback: ' + error?.message);
    }
  };

  const resetForm = () => {
    setFormData({
      titolo: '',
      descrizione: '',
      tipo: 'percentuale',
      valore: '',
      importoMinimo: '',
      importoMassimo: '',
      condizioni: '',
      dataInizio: '',
      dataFine: '',
      stato: 'attiva',
      inEvidenza: false
    });
    setEditingCashback(null);
    setShowForm(false);
  };

  const getStatoBadge = (stato) => {
    const badges = {
      attiva: { color: 'bg-green-100 text-green-700', label: 'Attiva' },
      sospesa: { color: 'bg-yellow-100 text-yellow-700', label: 'Sospesa' },
      scaduta: { color: 'bg-gray-100 text-gray-700', label: 'Scaduta' }
    };
    return badges?.[stato] || badges?.attiva;
  };

  if (loading) {
    return <div className="text-center py-8">Caricamento offerte cashback...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Offerte Cashback ({cashbackOffers?.length})</h2>
        <Button onClick={() => setShowForm(!showForm)}>
          <Icon name={showForm ? 'X' : 'Plus'} size={20} />
          {showForm ? 'Annulla' : 'Aggiungi Cashback'}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-muted/30 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingCashback ? 'Modifica Cashback' : 'Nuovo Cashback'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Titolo Offerta *"
              value={formData?.titolo}
              onChange={(e) => setFormData({ ...formData, titolo: e?.target?.value })}
              required
              className="md:col-span-2"
            />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Descrizione</label>
              <textarea
                value={formData?.descrizione}
                onChange={(e) => setFormData({ ...formData, descrizione: e?.target?.value })}
                rows={2}
                className="w-full px-4 py-2 rounded-lg border border-input bg-background"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Tipo Cashback</label>
              <select
                value={formData?.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e?.target?.value })}
                className="w-full px-4 py-2 rounded-lg border border-input bg-background"
              >
                <option value="percentuale">Percentuale</option>
                <option value="fisso">Fisso</option>
                <option value="progressivo">Progressivo</option>
              </select>
            </div>
            <Input
              label="Valore *"
              type="number"
              step="0.01"
              value={formData?.valore}
              onChange={(e) => setFormData({ ...formData, valore: e?.target?.value })}
              required
              placeholder={formData?.tipo === 'percentuale' ? '% cashback' : '€ cashback'}
            />
            <Input
              label="Importo Minimo (€)"
              type="number"
              step="0.01"
              value={formData?.importoMinimo}
              onChange={(e) => setFormData({ ...formData, importoMinimo: e?.target?.value })}
            />
            <Input
              label="Importo Massimo (€)"
              type="number"
              step="0.01"
              value={formData?.importoMassimo}
              onChange={(e) => setFormData({ ...formData, importoMassimo: e?.target?.value })}
            />
            <Input
              label="Data Inizio"
              type="date"
              value={formData?.dataInizio}
              onChange={(e) => setFormData({ ...formData, dataInizio: e?.target?.value })}
            />
            <Input
              label="Data Fine"
              type="date"
              value={formData?.dataFine}
              onChange={(e) => setFormData({ ...formData, dataFine: e?.target?.value })}
            />
            <div>
              <label className="block text-sm font-medium mb-2">Stato</label>
              <select
                value={formData?.stato}
                onChange={(e) => setFormData({ ...formData, stato: e?.target?.value })}
                className="w-full px-4 py-2 rounded-lg border border-input bg-background"
              >
                <option value="attiva">Attiva</option>
                <option value="sospesa">Sospesa</option>
                <option value="scaduta">Scaduta</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Condizioni</label>
              <textarea
                value={formData?.condizioni}
                onChange={(e) => setFormData({ ...formData, condizioni: e?.target?.value })}
                rows={2}
                className="w-full px-4 py-2 rounded-lg border border-input bg-background"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData?.inEvidenza}
                  onChange={(e) => setFormData({ ...formData, inEvidenza: e?.target?.checked })}
                  className="rounded"
                />
                <span className="text-sm">In Evidenza</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <Button type="submit">
              <Icon name="Save" size={20} />
              {editingCashback ? 'Aggiorna' : 'Salva'}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm}>
              Annulla
            </Button>
          </div>
        </form>
      )}

      {cashbackOffers?.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="DollarSign" size={48} className="mx-auto mb-4 opacity-50" />
          <p>Nessuna offerta cashback presente</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cashbackOffers?.map((cashback) => {
            const statoBadge = getStatoBadge(cashback?.stato);
            return (
              <div key={cashback?.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-lg">{cashback?.titolo}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${statoBadge?.color}`}>
                        {statoBadge?.label}
                      </span>
                    </div>
                    {cashback?.descrizione && (
                      <p className="text-sm text-muted-foreground mb-3">{cashback?.descrizione}</p>
                    )}
                  </div>
                  <div className="flex gap-2 ml-2">
                    <button
                      onClick={() => handleEdit(cashback)}
                      className="p-2 hover:bg-muted rounded-lg transition-smooth"
                    >
                      <Icon name="Edit" size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(cashback?.id)}
                      className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-smooth"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </div>
                <div className="bg-primary/10 rounded-lg p-3 mb-3">
                  <div className="text-2xl font-bold text-primary">
                    {cashback?.tipo === 'percentuale' ? `${cashback?.valore}%` : `€${cashback?.valore}`}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {cashback?.tipo === 'percentuale' ? 'Cashback Percentuale' : cashback?.tipo === 'fisso' ? 'Cashback Fisso' : 'Cashback Progressivo'}
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  {cashback?.importoMinimo && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="ArrowUp" size={14} />
                      <span>Minimo: €{cashback?.importoMinimo}</span>
                    </div>
                  )}
                  {cashback?.importoMassimo && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="ArrowDown" size={14} />
                      <span>Massimo: €{cashback?.importoMassimo}</span>
                    </div>
                  )}
                  {(cashback?.dataInizio || cashback?.dataFine) && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="Calendar" size={14} />
                      <span>
                        {cashback?.dataInizio && format(new Date(cashback?.dataInizio), 'dd/MM/yyyy')}
                        {cashback?.dataInizio && cashback?.dataFine && ' - '}
                        {cashback?.dataFine && format(new Date(cashback?.dataFine), 'dd/MM/yyyy')}
                      </span>
                    </div>
                  )}
                  {cashback?.condizioni && (
                    <p className="text-xs text-muted-foreground mt-2">📝 {cashback?.condizioni}</p>
                  )}
                </div>
                {cashback?.inEvidenza && (
                  <div className="mt-3">
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">In Evidenza</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CashbackManager;